import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';

import { concat, find, findIndex, first, get, slice, startsWith } from 'lodash';

import { uploadImage } from 'utils/files';
import { notifyErrors } from 'utils/notifications';

import { fetchAssignmentSections, uploadAssignmentFeedback } from './sdk';

// Finds the object in the list and returns it, or returns undefined.
export const getObjectFromList = (objList, id) => {
  return find(objList, { id: parseInt(id) });
};

export const getObjectFromListOrFirst = (objList, id) => {
  return getObjectFromList(objList, id) || first(objList);
};

export const getPreviousTask = (tasks, activeTask) => {
  const index = findIndex(tasks, (task) => task.id === activeTask.id);
  return index > 0 ? tasks[index - 1] : tasks[tasks.length - 1];
};

export const getNextTask = (tasks, activeTask) => {
  const index = findIndex(tasks, (task) => task.id === activeTask.id);
  return index >= 0 && index < tasks.length - 1 ? tasks[index + 1] : tasks[0];
};

export const getNextNonGradedTask = ({
  tasks,
  activeTask,
  excludedTaskIds
}) => {
  let lookupTasks = tasks;

  if (activeTask) {
    const index = findIndex(tasks, (task) => task.id === activeTask.id);

    const afterIndexSlice = slice(tasks, index + 1);
    const beforeIndexSlice = slice(tasks, 0, index);

    lookupTasks = concat(afterIndexSlice, beforeIndexSlice);
  }

  const validStatuses = new Set(['submitted', 'revise']);
  const excludedIds = new Set([...excludedTaskIds, activeTask?.id]);

  const nextTask = find(
    lookupTasks,
    (task) => validStatuses.has(task.status) && !excludedIds.has(task.id)
  );

  return nextTask;
};

export const getNextNonGradedStudent = ({
  studentWorks,
  activeStudentId,
  activeTask,
  excludedTaskIds
}) => {
  const validStatuses = new Set(['submitted', 'revise']);
  const excludedIds = new Set([...excludedTaskIds, activeTask.id]);

  const nextStudent = find(
    studentWorks,
    (studentWork) =>
      studentWork.student.id !== activeStudentId &&
      studentWork.work.some(
        (w) =>
          validStatuses.has(w.status) &&
          !excludedIds.has(w.tracker_score_response_id)
      )
  );

  return get(nextStudent, 'student', null);
};

/**
 * Returns:
 *   [`sections`: Array<Section>, `fetchSections`: (trackerId: number) => Promise]
 */
export const useSections = () => {
  const [sections, setSections] = useState([]);

  const fetchSections = (trackerId) => {
    return fetchAssignmentSections(trackerId).then(
      ({ success, data, errors }) => {
        if (success) {
          setSections(data);
          return data;
        } else {
          notifyErrors(errors);
        }
      }
    );
  };

  const [response, setResponse] = useState([sections, fetchSections]);

  useEffect(() => {
    setResponse([sections, fetchSections]);
  }, [sections]);

  return response;
};

export const loadImageFromSrc = (src) =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      resolve(image);
    };
    image.crossOrigin = 'Anonymous';
    image.onerror = (error) => {
      notifyErrors(['There was an error in loading some images']);
      Sentry.captureException(
        new Error('There was an error in loading the next image'),
        {
          extra: {
            error,
            src: image.src
          }
        }
      );
    };

    if (!startsWith(src, 'blob')) {
      // When we call s3 for an image and we pass "crossOrigin=Anonymous"
      // we get back a CORS error and not the image. That usually happens if the image was previously cached
      // by the browser (only in Chrome) and it was cached without the "crossOrigin = Anonymous"
      // To fix this - we have to send another request with a random GET parameter.
      // The key and value of the GET parameter doesn't matter. We just use it to tell Chrome to NOT
      // get the image from it's cache this time
      //https://serverfault.com/questions/856904/chrome-s3-cloudfront-no-access-control-allow-origin-header-on-initial-xhr-req
      image.src = `${src}?dont-cache=true`;
    }
  });

const saveImage = async (blob, activeWork, feedbackTexts) => {
  const feedbackFileId = await uploadImage({
    blob,
    name: 'feedback'
  });

  const data = await uploadAssignmentFeedback({
    feedback_file_id: feedbackFileId,
    original_work: activeWork.original_id || activeWork.id,
    work_id: activeWork.id,
    feedback_texts: feedbackTexts
  });

  return data;
};

export const uploadImageBlob = async (blob, activeWork, feedbackTexts) => {
  const res = await saveImage(blob, activeWork, feedbackTexts);

  return res;
};
